/* global APP */

import Logger from 'meet-hour-logger';

import UIEvents from '../../../service/UI/UIEvents';
import { getActiveDOMDocument } from '../base/config/functions.any';
import { MEDIA_TYPE, VIDEO_TYPE } from '../base/media';
import {
    getLocalParticipant as getLocalParticipantFromStore,
    getParticipantById,
    getPinnedParticipant,
    isScreenShareParticipantById,
    pinParticipant
} from '../base/participants';
import { getLocalDesktopTrack, getTrackByMediaTypeAndParticipant } from '../base/tracks';
import { isVideoPlayable } from '../filmstrip';
import { updateThumbnailZoomTransform } from '../filmstrip/functions.web';
import { LargeVideoManager, VIDEO_CONTAINER_TYPE } from '../large-video';
import { getLocalVideoZoom, getRemoteVideoZoom } from '../zoom-sync';

import { LAYOUTS } from './constants';

const logger = Logger.getLogger(__filename);

const noop = () => {
    // No-op for React managed filmstrip components.
};

/**
 * Helper to retrieve thumbnail container element from DOM.
 *
 * @param {string} videoSpanId - Container element ID.
 * @returns {HTMLElement|null}
 */
function getThumbnailContainer(videoSpanId) {
    if (typeof document === 'undefined' || !videoSpanId) {
        return null;
    }

    const state = APP.store && APP.store.getState ? APP.store.getState() : null;
    const activeWindow = state ? getActiveDOMDocument(state) : document;

    return activeWindow?.getElementById(videoSpanId) || document.getElementById(videoSpanId) || null;
}

/**
 * Applies the synchronized zoom from redux to a thumbnail outside React.
 *
 * @param {HTMLElement} container - The thumbnail container.
 * @param {string} id - The participant ID.
 * @param {boolean} isLocal - Whether the thumbnail is the local one.
 * @returns {void}
 */
function syncThumbnailZoom(container, id, isLocal) {
    const state = APP.store.getState();
    const local = Boolean(isLocal || APP.conference?.isLocalId(id));
    const { filmStripView, objectFit } = state['features/base/conference'] || {};

    updateThumbnailZoomTransform({
        container,
        isLocal: local,
        localFlipX: state['features/base/settings']?.localFlipX,
        objectFit: filmStripView === LAYOUTS.TILE_VIEW ? objectFit || 'cover' : 'cover',
        zoomState: local
            ? getLocalVideoZoom(state, VIDEO_TYPE.CAMERA)
            : getRemoteVideoZoom(state, id, VIDEO_TYPE.CAMERA)
    });
}

const remoteVideos = {};
let localVideoThumbnail = null;

let eventEmitter = null;

let largeVideo;

/**
 * FlipX state of the localVideo.
 */
let localFlipX = null;

/**
 * Handler for local flip X changed event.
 *
 * @param {Object} val
 */
function onLocalFlipXChanged(val) {
    localFlipX = val;
    if (largeVideo) {
        largeVideo.onLocalFlipXChange(val);
    }
}

/**
 * Returns an array of all thumbnails in the filmstrip.
 *
 * @private
 * @returns {Array}
 */
function getAllThumbnails() {
    return [
        ...localVideoThumbnail ? [ localVideoThumbnail ] : [],
        ...Object.values(remoteVideos)
    ];
}

/**
 * Private helper to get the redux representation of the local participant.
 *
 * @private
 * @returns {Object}
 */
function getLocalParticipant() {
    return getLocalParticipantFromStore(APP.store.getState());
}

const VideoLayout = {
    init(emitter) {
        eventEmitter = emitter;

        localVideoThumbnail = {
            id: 'local',
            videoSpanId: 'localVideoContainer',
            getId: () => (APP.conference && APP.conference.getMyUserId()) || 'local',
            focus: noop,
            changeVideo: () => {
                const myUserId = APP.conference && APP.conference.getMyUserId();

                if (myUserId) {
                    this._updateLargeVideoIfDisplayed(myUserId);
                }
            },
            setVisible: noop,
            updateView: noop,
            updateTileViewClass: noop,
            onStateChange: noop,
            updateDOMLocation: noop,
            rerender: noop,
            togglePin: noop,
            setFlipX: val => onLocalFlipXChanged(val),
            updateZoomTransform: () => {
                const container = getThumbnailContainer('localVideoContainer')
                    || getThumbnailContainer('filmstripLocalVideoThumbnail')
                    || getThumbnailContainer('localVideoTileViewContainer');

                if (container) {
                    syncThumbnailZoom(container, 'local', true);
                }
            },
            addRemoteStreamElement: noop,
            removeRemoteStreamElement: noop,
            remove: noop,
            get container() {
                return getThumbnailContainer(this.videoSpanId);
            }
        };

        this.registerListeners();
    },

    setLocalVideo(thumbnail) {
        localVideoThumbnail = thumbnail;
    },

    /**
     * Registering listeners for UI events in Video layout component.
     *
     * @returns {void}
     */
    registerListeners() {
        eventEmitter.addListener(UIEvents.LOCAL_FLIPX_CHANGED,
            onLocalFlipXChanged);
    },

    /**
     * Cleans up state of this singleton {@code VideoLayout}.
     *
     * @returns {void}
     */
    reset() {
        this._resetLargeVideo();
        this._resetFilmstrip();
    },

    initLargeVideo() {
        this._resetLargeVideo();

        largeVideo = new LargeVideoManager(eventEmitter);
        if (localFlipX) {
            largeVideo.onLocalFlipXChange(localFlipX);
        }
        largeVideo.updateContainerSize();
    },

    /**
     * Sets the audio level of the video elements associated to the given id.
     *
     * @param id - The video identifier in the form it comes from the library.
     * @param lvl - The new audio level to update to.
     */
    setAudioLevel(id, lvl) {
        if (largeVideo && id === largeVideo.id) {
            largeVideo.updateLargeVideoAudioLevel(lvl);
        }
    },

    changeLocalVideo(stream) {
        const localParticipant = getLocalParticipant();
        const localId = localParticipant?.id;

        if (!localId || !localVideoThumbnail) {
            logger.warn('Skipping local video update before local thumbnail is ready.');

            return;
        }

        this.onVideoTypeChanged(localId, stream.videoType);

        try {
            localVideoThumbnail.changeVideo?.(stream);
        } catch (error) {
            logger.error('Failed to update local video thumbnail stream.', error);
        }

        this._updateLargeVideoIfDisplayed(localId);
    },

    /**
     * Shows/hides local video.
     *
     * @param {boolean} visible - To make the local video visible, false - otherwise.
     */
    setLocalVideoVisible(visible) {
        localVideoThumbnail?.setVisible?.(visible);
    },

    onRemoteStreamAdded(stream) {
        const id = stream.getParticipantId();
        const remoteVideo = remoteVideos[id];

        if (!remoteVideo) {
            logger.debug('No remote video element to add stream');

            return;
        }

        remoteVideo.addRemoteStreamElement?.(stream);

        this.onVideoMute(id);
        remoteVideo.updateView?.();
    },

    onScreenShareStreamAdded(stream) {
        const id = stream.getSourceName();
        const remoteVideo = remoteVideos[id];

        if (!remoteVideo) {
            logger.debug('No remote video element to add stream');

            return;
        }

        remoteVideo.addRemoteStreamElement?.(stream);

        this.onVideoMute(id);
        remoteVideo.updateView?.();
    },

    onRemoteStreamRemoved(stream) {
        const id = stream.getParticipantId();
        const remoteVideo = remoteVideos[id];

        // Remote stream may be removed after participant left the conference.
        if (remoteVideo) {
            remoteVideo.removeRemoteStreamElement?.(stream);
            remoteVideo.updateView?.();
        }

        this.updateVideoMutedForNoTracks(id);
    },

    /**
     * If participant has no tracks will make the UI display muted status.
     *
     * @param {string} participantId
     */
    updateVideoMutedForNoTracks(participantId) {
        const participant = APP.conference.getParticipantById(participantId);

        if (participant && !participant.getTracksByMediaType('video').length) {
            APP.UI.setVideoMuted(participantId);
        }
    },

    /**
     * Return the type of the remote video.
     *
     * @param id - The id for the remote video.
     * @returns {string} The video type video or screen.
     */
    getRemoteVideoType(id) {
        const state = APP.store.getState();
        const isScreenShare = isScreenShareParticipantById(state, id);

        if (isScreenShare) {
            return VIDEO_TYPE.DESKTOP;
        }

        const videoTrack = getTrackByMediaTypeAndParticipant(state['features/base/tracks'], MEDIA_TYPE.VIDEO, id);

        // Return the video type or default to CAMERA if undefined
        return videoTrack?.videoType || VIDEO_TYPE.CAMERA;
    },

    isPinned(id) {
        return id === this.getPinnedId();
    },

    getPinnedId() {
        const { id } = getPinnedParticipant(APP.store.getState()) || {};

        return id || null;
    },

    /**
     * Triggers a thumbnail to pin or unpin itself.
     *
     * @param {number} videoNumber - The index of the video to toggle pin on.
     * @private
     */
    togglePin(videoNumber) {
        const videos = getAllThumbnails();
        const videoView = videos[videoNumber];

        videoView?.togglePin?.();
    },

    /**
     * Callback invoked to update display when the pin participant has changed.
     *
     * @param {string|null} pinnedParticipantID - The participant ID of the
     * participant that is pinned or null if no one is pinned.
     * @returns {void}
     */
    onPinChange(pinnedParticipantID) {
        getAllThumbnails().forEach(thumbnail => {
            const thumbId = typeof thumbnail?.getId === 'function' ? thumbnail.getId() : thumbnail?.id;

            // Call focus for backward compatibility
            thumbnail?.focus?.(pinnedParticipantID === thumbId);

            // Also trigger updateView to ensure state-based pinning logic is applied
            thumbnail?.updateView?.();
        });
    },

    /**
     * Creates a participant container for the given id.
     *
     * @param {Object} participant - The redux representation of a remote participant.
     * @returns {void}
     */
    addRemoteParticipantContainer(participant) {
        if (!participant || participant.local) {
            return;
        } else if (participant.isFakeParticipant && participant.fakeParticipant) {
            return;
        }

        const id = participant.id;
        const remoteVideo = {
            id,
            videoSpanId: `participant_${id}`,
            getId: () => id,
            focus: noop,
            updateView: noop,
            isVideoPlayable: () => isVideoPlayable(APP.store.getState(), id),
            addRemoteStreamElement: noop,
            removeRemoteStreamElement: noop,
            remove: noop,
            setVisible: noop,
            changeVideo: noop,
            updateTileViewClass: noop,
            onStateChange: noop,
            updateDOMLocation: noop,
            rerender: noop,
            togglePin: noop,
            get container() {
                return getThumbnailContainer(this.videoSpanId);
            }
        };

        this.addRemoteVideoContainer(id, remoteVideo);
        this.updateVideoMutedForNoTracks(id);
    },

    addScreenShareParticipantContainer(participant) {
        // Skip regular local participants (not screen shares)
        if (participant.local && !participant.isFakeParticipant) {
            return;
        }

        const id = participant.id;
        const remoteVideo = {
            id,
            videoSpanId: `participant_${id}`,
            getId: () => id,
            focus: noop,
            updateView: noop,
            isVideoPlayable: () => {
                const state = APP.store.getState();
                const tracks = state['features/base/tracks'] || [];
                const isLocal = participant?.local || participant?.isFakeParticipant === 'LocalScreenShare';
                const track = isLocal
                    ? getLocalDesktopTrack(tracks)
                    : getTrackByMediaTypeAndParticipant(tracks, MEDIA_TYPE.SCREENSHARE, id);
                const hasValidTrack = track && track.mhTrack && track.mhTrack.stream && !track.muted;

                return Boolean(hasValidTrack || isVideoPlayable(state, id));
            },
            addRemoteStreamElement: noop,
            removeRemoteStreamElement: noop,
            remove: noop,
            setVisible: noop,
            changeVideo: noop,
            updateTileViewClass: noop,
            onStateChange: noop,
            updateDOMLocation: noop,
            rerender: noop,
            togglePin: noop,
            get container() {
                return getThumbnailContainer(this.videoSpanId);
            }
        };

        this.addRemoteVideoContainer(id, remoteVideo);
        this.updateVideoMutedForNoTracks(id);
    },

    addRemoteVideoContainer(id, remoteVideo) {
        remoteVideos[id] = remoteVideo;

        // Initialize the view
        remoteVideo?.updateView?.();
    },

    /**
     * On video muted event.
     */
    onVideoMute(id) {
        if (APP.conference.isLocalId(id)) {
            localVideoThumbnail?.updateView?.();
        } else {
            const remoteVideo = remoteVideos[id];

            if (remoteVideo) {
                remoteVideo?.updateView?.();
            }
        }

        // large video will show avatar instead of muted stream
        this._updateLargeVideoIfDisplayed(id, true);
    },

    /**
     * Shows/hides warning about a user's connectivity issues.
     *
     * @param {string} id - The ID of the remote participant(MUC nickname).
     * @returns {void}
     */
    onParticipantConnectionStatusChanged(id) {
        if (APP.conference.isLocalId(id)) {
            return;
        }

        // We have to trigger full large video update to transition from
        // avatar to video on connectivity restored.
        this._updateLargeVideoIfDisplayed(id, true);

        const remoteVideo = remoteVideos[id];

        if (remoteVideo) {
            remoteVideo?.updateView?.();
        }
    },

    /**
     * On last N change event.
     *
     * @param endpointsLeavingLastN - The list currently leaving last N endpoints.
     * @param endpointsEnteringLastN - The list currently entering last N endpoints.
     */
    onLastNEndpointsChanged(endpointsLeavingLastN, endpointsEnteringLastN) {
        if (endpointsLeavingLastN) {
            endpointsLeavingLastN.forEach(this._updateRemoteVideo, this);
        }

        if (endpointsEnteringLastN) {
            endpointsEnteringLastN.forEach(this._updateRemoteVideo, this);
        }
    },

    /**
     * Updates remote video by id if it exists.
     *
     * @param {string} id - Of the remote video.
     * @private
     */
    _updateRemoteVideo(id) {
        const remoteVideo = remoteVideos[id];

        if (remoteVideo) {
            remoteVideo?.updateView?.();
            this._updateLargeVideoIfDisplayed(id);
        }
    },

    removeParticipantContainer(id) {
        // Unlock large video
        if (this.getPinnedId() === id) {
            APP.store.dispatch(pinParticipant(null));
        }

        this.unregisterRemoteVideoContainer(id);
    },

    /**
     * Forgets a thumbnail without unpinning its participant. A tile unmounts
     * whenever it drops past the tile limit, and that must not cost the pin.
     *
     * @param {string} id - The participant id.
     * @returns {void}
     */
    unregisterRemoteVideoContainer(id) {
        const remoteVideo = remoteVideos[id];

        if (remoteVideo) {
            delete remoteVideos[id];
            remoteVideo?.remove?.();
        } else {
            logger.warn(`No remote video for ${id}`);
        }
    },

    onVideoTypeChanged(id) {
        const remoteVideo = remoteVideos[id];

        if (!remoteVideo) {
            return;
        }

        remoteVideo?.updateView?.();
    },

    /**
     * Resizes the video area.
     */
    resizeVideoArea() {
        if (largeVideo) {
            largeVideo.updateContainerSize();
            largeVideo.resize(false);
            this.updateAllZoomTransforms();
        }
    },

    /**
     * Returns all active thumbnail instances.
     *
     * @returns {Array}
     */
    getAllThumbnails() {
        return getAllThumbnails();
    },

    /**
     * Updates the zoom transform on all thumbnails.
     *
     * @returns {void}
     */
    updateAllZoomTransforms() {
        getAllThumbnails().forEach(thumb => {
            thumb?.updateZoomTransform?.();
        });

        if (typeof document !== 'undefined') {
            const containers = document.querySelectorAll(
                '#filmstripLocalVideoThumbnail, #localVideoTileViewContainer, #filmstripRemoteVideosContainer .videocontainer'
            );

            containers.forEach(container => {
                const id = container.id?.replace(/^participant_/, '');
                const isLocal = container.id === 'localVideoContainer'
                    || container.id === 'filmstripLocalVideoThumbnail'
                    || container.id === 'localVideoTileViewContainer';

                syncThumbnailZoom(container, isLocal ? 'local' : id, isLocal);
            });
        }
    },

    getLocalVideo() {
        return localVideoThumbnail;
    },

    getSmallVideo(id) {
        if (!id || (APP.conference && APP.conference.isLocalId(id))) {
            return localVideoThumbnail;
        }

        const localPart = getLocalParticipant();

        if (localPart && localPart.id === id) {
            return localVideoThumbnail;
        }

        if (remoteVideos[id]) {
            return remoteVideos[id];
        }

        // Fallback adapter for React-managed containers
        const container = getThumbnailContainer(`participant_${id}`);

        if (container) {
            return {
                id,
                container,
                updateZoomTransform: () => {
                    syncThumbnailZoom(container, id, false);
                }
            };
        }

        return undefined;
    },

    changeUserAvatar(id, avatarUrl) {
        if (this.isCurrentlyOnLarge(id)) {
            largeVideo.updateAvatar(avatarUrl);
        }
    },

    isLargeVideoVisible() {
        return this.isLargeContainerTypeVisible(VIDEO_CONTAINER_TYPE);
    },

    /**
     * @returns {LargeContainer} The currently displayed container on large video.
     */
    getCurrentlyOnLargeContainer() {
        return largeVideo?.getCurrentContainer();
    },

    isCurrentlyOnLarge(id) {
        return largeVideo && largeVideo.id === id;
    },

    /**
     * Triggers an update of remote video and large video displays so they may
     * pick up any state changes that have occurred elsewhere.
     *
     * @returns {void}
     */
    updateAllVideos() {
        const displayedUserId = this.getLargeVideoID();

        if (displayedUserId) {
            this.updateLargeVideo(displayedUserId, true);
        }

        Object.keys(remoteVideos).forEach(video => {
            remoteVideos[video]?.updateView?.();
        });
    },

    /**
     * Updates tile view classes for all SmallVideo instances when participants count
     * or conference view changes. This ensures immediate visual updates.
     *
     * @returns {void}
     */
    updateAllTileViewClasses() {
        // Update local video tile view class
        if (localVideoThumbnail) {
            localVideoThumbnail?.updateTileViewClass?.();
        }

        // Update all remote video tile view classes
        Object.keys(remoteVideos).forEach(video => {
            remoteVideos[video]?.updateTileViewClass?.();
        });
    },

    /**
     * Triggers state change updates for all SmallVideo instances.
     * This method should be called when participants count or conference view changes.
     *
     * @returns {void}
     */
    onParticipantsOrViewStateChange() {
        // Update local video
        if (localVideoThumbnail) {
            localVideoThumbnail?.onStateChange?.();
        }

        // Update all remote videos
        Object.keys(remoteVideos).forEach(video => {
            remoteVideos[video]?.onStateChange?.();
        });

    },

    updateLargeVideo(id, forceUpdate) {
        if (!largeVideo) {
            return;
        }
        const currentContainer = largeVideo.getCurrentContainer();
        const currentContainerType = largeVideo.getCurrentContainerType();
        const isOnLarge = this.isCurrentlyOnLarge(id);
        const state = APP.store.getState();
        const participant = getParticipantById(state, id);
        const isFakeScreenshare = participant?.isFakeParticipant === 'LocalScreenShare' || participant?.isFakeParticipant === 'RemoteScreenShare';

        const videoTrack = getTrackByMediaTypeAndParticipant(state['features/base/tracks'],
            isFakeScreenshare ? MEDIA_TYPE.SCREENSHARE : MEDIA_TYPE.VIDEO, id);
        const videoStream = videoTrack?.mhTrack;

        let shouldForceUpdate = forceUpdate;

        if (isOnLarge && !shouldForceUpdate
                && LargeVideoManager.isVideoContainer(currentContainerType)
                && videoStream) {
            const currentStreamId = currentContainer?.getStreamID?.();
            const newStreamId = videoStream?.getId() || null;

            if (currentStreamId !== newStreamId) {
                logger.debug('Enforcing large video update for stream change');
                shouldForceUpdate = true;
            }
        }

        if (!isOnLarge || shouldForceUpdate) {
            const videoType = this.getRemoteVideoType(id);

            largeVideo.updateLargeVideo(
                id,
                videoStream,
                videoType || VIDEO_TYPE.CAMERA
            ).catch(() => {
                // do nothing
            });
        }
    },

    addLargeVideoContainer(type, container) {
        largeVideo && largeVideo.addContainer(type, container);
    },

    removeLargeVideoContainer(type) {
        largeVideo && largeVideo.removeContainer(type);
    },

    /**
     * @returns {Promise}
     */
    showLargeVideoContainer(type, show) {
        if (!largeVideo) {
            return Promise.reject();
        }

        const isVisible = this.isLargeContainerTypeVisible(type);

        if (isVisible === show) {
            return Promise.resolve();
        }

        const currentId = largeVideo.id;
        let oldSmallVideo;

        if (currentId) {
            oldSmallVideo = this.getSmallVideo(currentId);
        }

        let containerTypeToShow = type;

        if (!show) {
            const pinnedId = this.getPinnedId();

            if (pinnedId) {
                containerTypeToShow = this.getRemoteVideoType(pinnedId);
            } else {
                containerTypeToShow = VIDEO_CONTAINER_TYPE;
            }
        }

        return largeVideo.showContainer(containerTypeToShow)
            .then(() => {
                if (oldSmallVideo) {
                    oldSmallVideo?.updateView?.();
                }
            });
    },

    isLargeContainerTypeVisible(type) {
        return largeVideo && largeVideo.state === type;
    },

    /**
     * Returns the id of the current video shown on large.
     */
    getLargeVideoID() {
        return largeVideo && largeVideo.id;
    },

    /**
     * Returns the current video shown on large.
     */
    getLargeVideo() {
        return largeVideo;
    },

    /**
     * Sets the flipX state of the local video.
     *
     * @param {boolean} val - For flipped otherwise false.
     */
    setLocalFlipX(val) {
        this.localFlipX = val;
        onLocalFlipXChanged(val);
        if (localVideoThumbnail) {
            localVideoThumbnail?.setFlipX?.(val);
        }
    },

    /**
     * Returns the wrapper jquery selector for the largeVideo.
     *
     * @returns {*}
     */
    getLargeVideoWrapper() {
        return this.getCurrentlyOnLargeContainer()?.$wrapper;
    },

    /**
     * Returns the number of remote video ids.
     *
     * @returns {number} The number of remote videos.
     */
    getRemoteVideosCount() {
        return Object.keys(remoteVideos).length;
    },

    /**
     * Helper method to invoke when the video layout has changed and elements
     * have to be re-arranged and resized.
     *
     * @returns {void}
     */
    refreshLayout() {
        localVideoThumbnail?.updateDOMLocation?.();
        this.resizeVideoArea();

        // Rerender the thumbnails since they are dependant on the layout because of the tooltip positioning.
        localVideoThumbnail?.rerender?.();
        Object.values(remoteVideos).forEach(remoteVideoThumbnail => remoteVideoThumbnail?.rerender?.());
    },

    /**
     * Cleans up any existing largeVideo instance.
     *
     * @private
     * @returns {void}
     */
    _resetLargeVideo() {
        if (largeVideo) {
            largeVideo.destroy();
        }

        largeVideo = null;
    },

    /**
     * Cleans up filmstrip state.
     *
     * @private
     * @returns {void}
     */
    _resetFilmstrip() {
        Object.keys(remoteVideos).forEach(remoteVideoId => {
            this.removeParticipantContainer(remoteVideoId);
            delete remoteVideos[remoteVideoId];
        });

        if (localVideoThumbnail) {
            localVideoThumbnail?.remove?.();
            localVideoThumbnail = null;
        }
    },

    /**
     * Triggers an update of large video if the passed in participant is
     * currently displayed on large video.
     *
     * @param {string} participantId - The participant ID that should trigger an
     * update of large video if displayed.
     * @param {boolean} force - Whether or not the large video update should
     * happen no matter what.
     * @returns {void}
     */
    _updateLargeVideoIfDisplayed(participantId, force = false) {
        if (this.isCurrentlyOnLarge(participantId)) {
            this.updateLargeVideo(participantId, force);
        }
    },

    /**
     * Handles window resizes.
     */
    onResize() {
        this.resizeVideoArea();
    }
};

export default VideoLayout;
